// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Network, Download, ZoomIn, Eye, EyeOff, MessageSquare } from 'lucide-react';
import ForceGraph2D from 'react-force-graph-2d';
import { apiJSON } from '../config/api';
import { telechargerFichierApi } from '../utils/telechargementApi';
import { handleApiError } from '../utils/notificationService';
import { useAppContext } from '../context/AppContext';
import './GrapheConceptuel.css';

const MIN_CO_OCCURRENCE_DEFAUT = 2;
const MAX_NOEUDS_DEFAUT = 200;
const HAUTEUR_GRAPHE = 560;

/** Largeur du canevas : pleine largeur sous la rupture mobile, moitié d'écran au-delà. */
function largeurGraphe() {
	if (typeof window === 'undefined') return 800;
	const w = window.innerWidth;
	// 768 px : même rupture que la feuille de style (colonne unique).
	if (w <= 768) return Math.max(280, w - 32);
	return Math.min(920, Math.floor(w * 0.5));
}

const GrapheConceptuel = () => {
	const [graphData, setGraphData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const { setChatDraftQuery } = useAppContext();
	
	const [minCoOccurrence, setMinCoOccurrence] = useState(MIN_CO_OCCURRENCE_DEFAUT);
	const [maxNodes, setMaxNodes] = useState(MAX_NOEUDS_DEFAUT);
	const [largeur, setLargeur] = useState(largeurGraphe);
	
	const [selectedCluster, setSelectedCluster] = useState(null);
	const [minCentrality, setMinCentrality] = useState(0);
	const [showLabels, setShowLabels] = useState(true);
	
	const [selectedConcept, setSelectedConcept] = useState(null);
	const [conceptPassages, setConceptPassages] = useState([]);
	const [passagesLoading, setPassagesLoading] = useState(false);
	const [passagesError, setPassagesError] = useState('');
	const [hoverNode, setHoverNode] = useState(null);
	const fgRef = useRef(null);

	// Paramètres passés explicitement : la fonction ne dépend d'aucun état, ce
	// qui permet de l'appeler au montage sans omettre de dépendance de
	// `useEffect` (elle l'était auparavant, en silence).
	const chargerGraphe = useCallback(async (minCo, maxN) => {
		setLoading(true);
		setError('');
		try {
			const qs = new URLSearchParams({
				min_co_occurrence: String(minCo),
				max_nodes: String(maxN),
			});
			// `apiJSON` porte les en-têtes d'authentification et lève sur un statut
			// non-2xx : le `fetch` brut précédent avalait un 503 en le traitant
			// comme une réponse valide.
			const data = await apiJSON(`graphs/concepts?${qs.toString()}`);

			if (data?.error) {
				setError(data.error);
				setGraphData(null);
			} else {
				setGraphData(data);
			}
		} catch (err) {
			setError(err?.message || 'Erreur génération graphe');
			setGraphData(null);
		} finally {
			setLoading(false);
		}
	}, []);

	const generateGraph = () => chargerGraphe(minCoOccurrence, maxNodes);

	const filteredGraphData = useMemo(() => {
		if (!graphData?.nodes?.length) {
			return { nodes: [], links: [] };
		}
		const nodes = graphData.nodes.filter((n) => {
			if (selectedCluster != null && n.cluster_id !== selectedCluster) return false;
			if ((n.centrality ?? 0) < minCentrality) return false;
			return true;
		});
		const ids = new Set(nodes.map((n) => String(n.id)));
		const rawLinks = graphData.edges || [];
		const links = rawLinks.filter(
			(e) => ids.has(String(e.source)) && ids.has(String(e.target))
		);
		return {
			nodes: nodes.map((n) => ({
				...n,
				id: String(n.id),
			})),
			links: links.map((e) => ({
				source: String(e.source),
				target: String(e.target),
				weight: e.weight,
			})),
		};
	}, [graphData, selectedCluster, minCentrality]);

	const handleNodeClick = useCallback(async (node) => {
		const conceptId = node.id;
		setSelectedConcept(conceptId);
		setPassagesLoading(true);
		setPassagesError('');
		setChatDraftQuery(`Explique le concept « ${node.label || conceptId} » à partir des documents indexés.`);

		try {
			const data = await apiJSON(
				`graphs/concepts/${encodeURIComponent(conceptId)}/passages`
			);
			setConceptPassages(data.passages || []);
		} catch (err) {
			// Une liste vide affichée après un échec réseau se confondait avec
			// « ce concept n'apparaît dans aucun passage ».
			setPassagesError(err?.message || 'Passages indisponibles');
			setConceptPassages([]);
		} finally {
			setPassagesLoading(false);
		}
	}, [setChatDraftQuery]);

	const openInReader = (passage) => {
		const bookId = passage.book_id;
		const chunkIdx = passage.chunk_idx;
		const cs = passage.char_start || 0;
		const ce = passage.char_end || 0;
		const url = `/lecteur?book=${bookId}&chunk=${chunkIdx}` + 
			(cs ? `&cs=${cs}&ce=${ce}` : '') + 
			'&highlight=true';
		window.open(url, '_blank');
	};

	const exportGraph = async (format) => {
		try {
			const qs = new URLSearchParams({
				format,
				min_co_occurrence: String(minCoOccurrence),
				max_nodes: String(maxNodes),
			});
			// `fetch(url)` brut : ni en-tête d'authentification (401 sous auth), ni
			// contrôle de `resp.ok` — une page d'erreur était enregistrée en .graphml.
			await telechargerFichierApi(`graphs/concepts?${qs.toString()}`, `concept_graph.${format}`);
		} catch (err) {
			handleApiError(err, 'Export du graphe');
		}
	};

	useEffect(() => {
		chargerGraphe(MIN_CO_OCCURRENCE_DEFAUT, MAX_NOEUDS_DEFAUT);
	}, [chargerGraphe]);

	useEffect(() => {
		const surRedimension = () => setLargeur(largeurGraphe());
		window.addEventListener('resize', surRedimension);
		return () => window.removeEventListener('resize', surRedimension);
	}, []);

	return (
		<div className="graphe-conceptuel app-page">
			<h1 className="graphe-conceptuel__titre">
				<Network size={24} aria-hidden /> Graphe Conceptuel
			</h1>

			<div className="graphe-conceptuel__filtres">
				<div className="graphe-conceptuel__champ">
					<label htmlFor="graphe-min-co">Co-occurrences min</label>
					<input
						id="graphe-min-co"
						type="number"
						min="1"
						max="10"
						value={minCoOccurrence}
						onChange={(e) => setMinCoOccurrence(parseInt(e.target.value, 10))}
					/>
				</div>

				<div className="graphe-conceptuel__champ">
					<label htmlFor="graphe-max-noeuds">Nœuds max</label>
					<input
						id="graphe-max-noeuds"
						type="number"
						min="50"
						max="1000"
						step="50"
						value={maxNodes}
						onChange={(e) => setMaxNodes(parseInt(e.target.value, 10))}
					/>
				</div>

				<div className="graphe-conceptuel__champ">
					<label htmlFor="graphe-cluster">Cluster</label>
					<select
						id="graphe-cluster"
						value={selectedCluster ?? ''}
						onChange={(e) => setSelectedCluster(e.target.value ? parseInt(e.target.value, 10) : null)}
					>
						<option value="">Tous</option>
						{graphData?.clusters?.map(cluster => (
							<option key={cluster.id} value={cluster.id}>
								{cluster.label} ({cluster.size} nœuds)
							</option>
						))}
					</select>
				</div>

				<div className="graphe-conceptuel__champ">
					<label htmlFor="graphe-centralite">Centralité min</label>
					<input
						id="graphe-centralite"
						type="range"
						min="0"
						max="1"
						step="0.1"
						value={minCentrality}
						onChange={(e) => setMinCentrality(parseFloat(e.target.value))}
					/>
					<output htmlFor="graphe-centralite" className="graphe-conceptuel__valeur">
						{minCentrality}
					</output>
				</div>

				<div className="graphe-conceptuel__actions">
					<button type="button" onClick={generateGraph} disabled={loading}>
						{loading ? 'Génération...' : 'Régénérer'}
					</button>

					<button
						type="button"
						className={`graphe-conceptuel__bascule${showLabels ? ' is-actif' : ''}`}
						onClick={() => setShowLabels(!showLabels)}
						aria-pressed={showLabels}
						aria-label="Afficher les étiquettes"
						title="Afficher les étiquettes"
					>
						{showLabels ? <Eye size={16} aria-hidden /> : <EyeOff size={16} aria-hidden />}
					</button>

					<button
						type="button"
						onClick={() => exportGraph('graphml')}
						aria-label="Exporter en GraphML"
						title="Exporter en GraphML"
					>
						<Download size={16} aria-hidden />
					</button>
					<button
						type="button"
						onClick={() => fgRef.current?.zoomToFit?.(400)}
						aria-label="Réajuster le zoom"
						title="Réajuster le zoom"
					>
						<ZoomIn size={16} aria-hidden />
					</button>
				</div>
			</div>

			{error && (
				<div role="alert" data-testid="graphe-erreur" className="graphe-conceptuel__erreur">
					{error}
				</div>
			)}

			<div className="graphe-conceptuel__corps">
				<div className="graphe-conceptuel__canevas">
					{filteredGraphData.nodes.length === 0 ? (
						<div data-testid="graphe-vide" className="graphe-conceptuel__vide">
							{loading
								? 'Chargement…'
								: error
									? 'Graphe indisponible'
									: 'Aucun nœud (ajustez les filtres ou régénérez)'}
						</div>
					) : (
						<ForceGraph2D
							ref={fgRef}
							width={largeur}
							height={HAUTEUR_GRAPHE}
							graphData={filteredGraphData}
							nodeLabel={(n) => n.label || n.id}
							nodeColor={(n) => n.color || '#818cf8'}
							nodeRelSize={5}
							linkWidth={(l) => Math.min(3, Math.sqrt((l.weight || 1)) * 0.4)}
							linkDirectionalParticles={1}
							linkDirectionalParticleSpeed={0.004}
							onNodeHover={setHoverNode}
							onNodeClick={handleNodeClick}
							{...(showLabels
								? {
										nodeCanvasObjectMode: () => 'after',
										nodeCanvasObject: (node, ctx, globalScale) => {
											const label = node.label || node.id;
											const fontSize = 12 / globalScale;
											ctx.font = `${fontSize}px Sans-Serif`;
											const textWidth = ctx.measureText(label).width;
											const bckgDimensions = [textWidth, fontSize].map((x) => x + fontSize * 0.4);
											ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
											ctx.fillRect(
												node.x - bckgDimensions[0] / 2,
												node.y - bckgDimensions[1] / 2,
												bckgDimensions[0],
												bckgDimensions[1]
											);
											ctx.textAlign = 'center';
											ctx.textBaseline = 'middle';
											ctx.fillStyle = '#e2e8f0';
											ctx.fillText(label, node.x, node.y);
										},
									}
								: {})}
							backgroundColor="#0f172a"
							cooldownTicks={120}
							onEngineStop={() => fgRef.current?.zoomToFit?.(400, 60)}
						/>
					)}
					{hoverNode && (
						<div className="graphe-conceptuel__survol">
							<strong>{hoverNode.label || hoverNode.id}</strong>
							{hoverNode.centrality != null && (
								<div className="graphe-conceptuel__survol-meta">
									Centralité : {hoverNode.centrality} · Cluster : {hoverNode.cluster_id ?? '—'}
								</div>
							)}
							<div className="graphe-conceptuel__survol-aide">
								Clic : passages + question dans l’IA
							</div>
						</div>
					)}
				</div>

				<div className="graphe-conceptuel__panneaux">
					{graphData && (
						<div className="graphe-conceptuel__carte">
							<h3>Statistiques</h3>
							<div className="graphe-conceptuel__stats">
								<div><strong>Concepts:</strong> {graphData.stats?.num_entities}</div>
								<div><strong>Connexions:</strong> {graphData.stats?.num_connections}</div>
								<div><strong>Clusters:</strong> {graphData.stats?.num_clusters}</div>
								<div><strong>Livres analysés:</strong> {graphData.stats?.num_books_analyzed}</div>
								<div><strong>Centralité moy.:</strong> {graphData.stats?.avg_centrality?.toFixed(3)}</div>
							</div>
						</div>
					)}

					{selectedConcept && (
						<div className="graphe-conceptuel__carte">
							<h3 className="graphe-conceptuel__concept">
								<MessageSquare size={20} aria-hidden /> {selectedConcept}
							</h3>
							<p className="graphe-conceptuel__note">
								La barre de question IA a été préremplie — ouvrez <strong>IA (RAG)</strong> pour envoyer.
							</p>
							{passagesLoading ? (
								<div>Chargement...</div>
							) : passagesError ? (
								<div role="alert" className="graphe-conceptuel__passages-erreur" data-testid="passages-erreur">
									{passagesError}
								</div>
							) : conceptPassages.length === 0 ? (
								<div className="graphe-conceptuel__note" data-testid="passages-vide">
									Aucun passage ne mentionne ce concept mot pour mot. Il provient
									des métadonnées du livre, pas du texte.
								</div>
							) : (
								<div className="graphe-conceptuel__passages">
									{conceptPassages.map((passage, i) => (
										<div key={i} className="graphe-conceptuel__passage">
											<div className="graphe-conceptuel__passage-titre">
												{passage.book_title}
											</div>
											<div className="graphe-conceptuel__passage-auteur">
												{passage.book_author}
											</div>
											<div className="graphe-conceptuel__passage-texte">
												{(passage.text || '').substring(0, 150)}...
											</div>
											<button
												type="button"
												className="graphe-conceptuel__ouvrir"
												onClick={() => openInReader(passage)}
											>
												Ouvrir dans lecteur
											</button>
										</div>
									))}
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default GrapheConceptuel;
